import type { FC } from 'react';
import type { PhotoProps } from './types';

import { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import { Wrapper } from '../Common';

import { ApiService, baseMediaEndpoint, generateAccessKey } from 'shared/helpers/index';
import { useAlert, useSales, useWebsiteEditor } from 'shared/hooks/index';

import { ConditionalImage } from '../ConditionalImage';
import { Image as ImageComp } from 'shared/components/Common';
import { useMutation } from 'react-query';

const Photo = styled.div`
  padding: 80px 0;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    padding: 60px 0;
    flex-direction: column-reverse;
  `}
`;

const Container = styled(Wrapper)`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ImageInner = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    width: -webkit-fill-available;
  `}
`;

const imageStyles = css`
  position: relative;
  overflow: hidden;
`;

const HoverableDropzoneStyled = styled(ConditionalImage)<{ width: number; height: number }>`
  max-width: 1300px;
  width: 100%;
  max-height: 960px;
  height: 560px;
  border: none;
  aspect-ratio: ${({ width, height }) => (width && height ? `${width} / ${height}` : '1300 / 560')};

  @media screen and (max-width: 1024px) {
    max-width: 100%;
  }

  ${({ theme: { $width } }) =>
    $width <= 1254 &&
    `
    max-width: 100%;
    max-height: 100%;
    width: 100% !important;
    height: auto !important;
  `}

  & img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center center;
  }
`;

export const V2: FC<PhotoProps> = (props) => {
  const { meta, hidden, id, customBackgroundColor, order } = props;

  const {
    editable,
    containerClassname,
    websiteAssembler,
    updateActiveElement,
    resetActiveElement
  } = useWebsiteEditor();

  const { addAlert } = useAlert();
  const { salesPageId, updateImageMeta, handleRemoveComponent, moveComponent, type, isDashboard } =
    useSales();
  const [resize, setResize] = useState({
    width: meta?.['width'] ?? 0,
    height: meta?.['height'] ?? 0
  });

  // Accessibility improvements
  const imageAccessKey = useMemo(() => generateAccessKey('phf', order), [order]);
  const skipLinkId = useMemo(() => `skip-to-photo-v2-${meta['accessorKey'] || 'photo'}`, [meta]);

  const handleEditError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Full-width photo edit error: ${error instanceof Error ? error.message : 'Edit action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const { mutateAsync: uploadeImage, isLoading } = useMutation({
    mutationFn: (payload: FormData) =>
      ApiService.post(`/sales/sales-page/${salesPageId}/media/`, payload),
    onError: () => {
      addAlert('Please upload png, jpg and jpeg image only', 'warning');
    }
  });

  const aboutMeImage = useMemo(() => {
    if (meta['image'] || meta['image_1']) {
      if (baseMediaEndpoint?.includes('localhost'))
        return `${baseMediaEndpoint}${meta['image'] || meta['image_1']}`;
      return meta['image'];
    }
    return '/img/lead_bg.png';
  }, [meta]);

  const handleUpload = async (file: File) => {
    if (!meta['accessorKey'] || !editable) return;

    try {
      const formData = new FormData();
      formData.append('type', meta['accessorKey']);
      formData.append('file', file);
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const width = img.width;
          const height = img.height;
          setResize({ width, height });
          updateImageMeta(meta['accessorKey'], width, 'width');
          updateImageMeta(meta['accessorKey'], height, 'height');
        };
        img.src = e.target?.result as string;
      };

      reader.readAsDataURL(file);

      const { data } = await uploadeImage(formData);
      updateImageMeta(meta['accessorKey'], data.file, 'image');
    } catch (error) {
      handleEditError(error);
      addAlert('Something went wrong', 'error');
    }
  };

  return (
    <Photo
      onClick={() => {
        if (id && isDashboard) {
          try {
            const selected = document.querySelector(`.${id}`);
            updateActiveElement({ currentTarget: selected } as never);
          } catch (error) {
            handleEditError(error);
          }
        }
      }}
      onBlur={resetActiveElement}
      id="no-editor-element"
      className={`${containerClassname} ${id}`}
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'flex',
        opacity: hidden ? 0.3 : 1,
        backgroundColor: customBackgroundColor || '#fff'
      }}
      data-accessor-parent={meta['accessorKey']}
      data-accessorkey="title"
      aria-label="Full-width photo section"
      role="region">
      {/* Skip link for keyboard navigation */}
      <span className="sr-only">
        <a href={`#${skipLinkId}`}>Skip to full-width photo</a>
      </span>

      <Container id={skipLinkId}>
        <ImageInner
          role="img"
          aria-label="Full-width featured photo"
          {...(!isDashboard && {
            accessKey: imageAccessKey,
            title: `Press Alt+${imageAccessKey} to focus the featured photo`,
            tabIndex: 0
          })}>
          <HoverableDropzoneStyled
            width={resize.width}
            height={resize.height}
            isSalePage
            css={imageStyles as never}
            disabled={!editable}
            enableLinks
            updateMeta={(value) => updateImageMeta(meta['accessorKey'], value, 'image_link')}
            onUpload={handleUpload}
            loading={isLoading}
            alreadyUploaded={Boolean(aboutMeImage)}
            imageFile={aboutMeImage}
            resizeSelector={editable}
            handleResize={({ width, height }) => {
              setResize({ width, height });
              updateImageMeta(meta['accessorKey'], width, 'width');
              updateImageMeta(meta['accessorKey'], height, 'height');
            }}
            initialPosition={{
              x: meta['x_1'] || 0,
              y: meta['y_1'] || 0,
              width: meta['d_width_1'],
              height: meta['d_height_1']
            }}
            meta={meta}
            positionEnabled
            handlePosition={(width: number, height: number, x: number, y: number) => {
              updateImageMeta(meta['accessorKey'], x, 'x_1');
              updateImageMeta(meta['accessorKey'], y, 'y_1');
              updateImageMeta(meta['accessorKey'], width, 'd_width_1');
              updateImageMeta(meta['accessorKey'], height, 'd_height_1');
            }}
            imageAspectRatio={{
              ratio: '5x2',
              pixels:
                resize.width && resize.height ? `${resize.width}x${resize.height}` : '1300x560'
            }}
            extraControls
            deleteComponent={() => handleRemoveComponent(meta['accessorKey'], type)}
            moveComponent={(direction: 1 | -1) =>
              moveComponent(direction, type, meta['accessorKey'])
            }>
            <ImageComp
              src={aboutMeImage}
              width={resize.width || 500}
              height={resize.height || 500}
              alt="Full-width featured photo displaying relevant visual content"
              role="img"
              aria-label="Featured photo in full-width layout"
            />
          </HoverableDropzoneStyled>
        </ImageInner>
      </Container>
    </Photo>
  );
};

V2.displayName = 'PhotoV2';
